import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Clapperboard, Music, Volume2, Mic, Image as ImageIcon, Video, Film, FolderOpen, Trash2, Plus, UploadCloud, Wand2, RotateCcw, X, CheckCircle2, AlertTriangle, Loader2, BookOpen, Sparkles } from 'lucide-react';
import { API, uploadFileToS3, formatFileSize } from './shared';

const CATS = [
  ['musique', 'Musiques', Music],
  ['bruitage', 'Bruitages', Volume2],
  ['voix', 'Voix', Mic],
  ['image', 'Images', ImageIcon],
  ['video', 'Vidéos', Video],
  ['modele', 'Modèles BA', Clapperboard],
  ['autre', 'Autres', FolderOpen],
];
const STYLES = ['Blockbuster action', 'Thriller haletant', 'Drame émotionnel', 'Comédie', 'Épique / Aventure', 'Horreur / Tension', 'Documentaire percutant', 'Teaser mystérieux', 'Familial / Jeunesse'];
const MOODS = ['Épique', 'Sombre', 'Émouvant', 'Joyeux', 'Angoissant', 'Inspirant', 'Nostalgique', 'Mystérieux'];
const AMBIANCES = ['Cinéma américain', 'Orchestral grandiose', 'Rythmé / percussif', 'Électro moderne', 'Minimaliste / silences', 'Chaleureux'];

const guessCat = (file) => {
  const t = file.type || '';
  if (t.startsWith('audio')) return 'musique';
  if (t.startsWith('image')) return 'image';
  if (t.startsWith('video')) return 'video';
  return 'autre';
};

function MediaCard({ m, light, onDelete }) {
  const hintCls = light ? 'text-gray-400' : 'text-white/35';
  return (
    <div className={`rounded-xl border p-3 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/5'}`} data-testid={`ts-media-${m.id}`}>
      {m.status === 'generating' ? (
        <div className="w-full h-9 mb-2 flex items-center gap-2 text-purple-400 text-[11px] font-semibold">
          <Loader2 size={13} className="animate-spin" /> Génération IA en cours…
        </div>
      ) : m.status === 'error' ? (
        <p className="text-[10px] text-red-400 mb-2">Échec : {m.error}</p>
      ) : m.category === 'image' ? (
        <img src={m.url} alt={m.name} className="w-full h-24 object-cover rounded-lg mb-2" />
      ) : (m.category === 'video' || m.category === 'modele') ? (
        <video src={m.url} className="w-full h-24 object-cover rounded-lg mb-2 bg-black" preload="metadata" controls muted />
      ) : (
        <audio src={m.url} className="w-full mb-2 h-9" controls preload="none" />
      )}
      <p className={`text-xs font-semibold truncate ${light ? 'text-gray-800' : 'text-white'}`} title={m.name}>
        {m.generated && <Sparkles size={10} className="inline mr-1 text-purple-400" />}{m.name}
      </p>
      <div className="flex items-center justify-between mt-1">
        <span className={`text-[10px] ${hintCls}`}>{formatFileSize(m.size || 0)}</span>
        <button onClick={() => onDelete(m)} data-testid={`ts-media-delete-${m.id}`}
          className="text-red-400 hover:text-red-300 transition-colors"><Trash2 size={12} /></button>
      </div>
    </div>
  );
}

function GenerateModal({ light, voices, onClose, onDone }) {
  const [gtype, setGtype] = useState('musique');
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(60);
  const [voiceId, setVoiceId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const inputCls = `w-full rounded-lg border px-3 py-2 text-xs outline-none ${light ? 'bg-gray-50 border-gray-200 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`;
  const labelCls = `block text-[11px] font-bold mb-1 ${light ? 'text-gray-600' : 'text-white/60'}`;
  const placeholders = {
    musique: 'Ex : musique de bande-annonce épique, orchestre sombre, tension montante, climax puissant, sans voix…',
    bruitage: 'Ex : boom cinématique profond, impact de trailer hollywoodien / riser de tension / whoosh…',
    voix: "Texte à dire. Ex : Dans un monde au bord du chaos, un homme va tout changer…",
  };
  const go = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/trailer-studio/media/generate`, {
        type: gtype, prompt: prompt.trim(), duration, voice_id: voiceId || undefined, name: name.trim(),
      }, { withCredentials: true });
      onDone();
    } catch (e) { alert(e?.response?.data?.detail || 'Échec de la génération'); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="ts-generate-modal">
      <div className={`w-full max-w-lg rounded-2xl border p-5 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`text-sm font-bold flex items-center gap-2 ${light ? 'text-gray-800' : 'text-white'}`}><Sparkles size={14} className="text-purple-400" /> Générer un média par IA (ElevenLabs)</h3>
          <button onClick={onClose} data-testid="ts-generate-close" className={light ? 'text-gray-400' : 'text-white/40'}><X size={16} /></button>
        </div>
        <div className="flex gap-1.5 mb-3">
          {[['musique', 'Musique', Music], ['bruitage', 'Bruitage', Volume2], ['voix', 'Voix', Mic]].map(([v, l, Icon]) => (
            <button key={v} onClick={() => setGtype(v)} data-testid={`ts-gen-type-${v}`}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${gtype === v ? 'bg-purple-600 text-white border-purple-600' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>
              <Icon size={11} /> {l}
            </button>
          ))}
        </div>
        <label className={labelCls}>{gtype === 'voix' ? 'Texte à dire' : 'Prompt (décrivez le son souhaité)'}</label>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3} placeholder={placeholders[gtype]}
          className={`${inputCls} resize-none mb-3`} data-testid="ts-gen-prompt" />
        <div className="grid grid-cols-2 gap-3 mb-3">
          {gtype !== 'voix' && (
            <div>
              <label className={labelCls}>Durée (s{gtype === 'musique' ? ', max 300' : ', max 22'})</label>
              <input type="number" min={gtype === 'musique' ? 10 : 1} max={gtype === 'musique' ? 300 : 22} value={duration}
                onChange={e => setDuration(+e.target.value || 0)} className={inputCls} data-testid="ts-gen-duration" />
            </div>
          )}
          {gtype === 'voix' && (
            <div>
              <label className={labelCls}>Voix</label>
              <select value={voiceId} onChange={e => setVoiceId(e.target.value)} className={inputCls} data-testid="ts-gen-voice">
                <option value="">Voix par défaut (grave cinéma)</option>
                {voices.map(v => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className={labelCls}>Nom (optionnel)</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Ex : Musique épique finale" className={inputCls} data-testid="ts-gen-name" />
          </div>
        </div>
        {gtype === 'musique' && <p className={`text-[10px] mb-3 ${light ? 'text-gray-400' : 'text-white/35'}`}>La musique est générée en arrière-plan (~30-60 s) et apparaîtra dans la bibliothèque. Consomme des crédits ElevenLabs.</p>}
        <div className="flex justify-end">
          <button onClick={go} disabled={busy || !prompt.trim()} data-testid="ts-gen-submit"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-purple-600 hover:bg-purple-500 text-white transition-colors disabled:opacity-40">
            <Sparkles size={12} /> {busy ? 'Génération…' : 'Générer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MediaLibrary({ light, media, counts, reload, voices }) {
  const [cat, setCat] = useState('');
  const [upCat, setUpCat] = useState('auto');
  const [uploads, setUploads] = useState([]);
  const [showGen, setShowGen] = useState(false);
  const fileRef = useRef(null);
  const hintCls = light ? 'text-gray-400' : 'text-white/35';

  useEffect(() => {
    if (!media.some(m => m.status === 'generating')) return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [media, reload]);

  const handleFiles = async (files) => {
    for (const file of Array.from(files)) {
      const label = file.name;
      setUploads(u => [...u, { label, pct: 0 }]);
      try {
        const fileUrl = await uploadFileToS3(file, (pct) => setUploads(u => u.map(x => x.label === label ? { ...x, pct } : x)));
        await axios.post(`${API}/admin/trailer-studio/media`, {
          name: file.name, category: upCat === 'auto' ? guessCat(file) : upCat,
          url: fileUrl, size: file.size, content_type: file.type,
        }, { withCredentials: true });
      } catch (e) { alert(`Échec de l'upload de ${label} : ${e?.response?.data?.detail || e.message}`); }
      setUploads(u => u.filter(x => x.label !== label));
    }
    reload();
  };

  const shown = media.filter(m => !cat || m.category === cat);
  return (
    <div data-testid="ts-library">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setCat('')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${!cat ? 'bg-[#E9641A] text-white border-[#E9641A]' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>Tous ({media.length})</button>
          {CATS.map(([v, l, Icon]) => (
            <button key={v} onClick={() => setCat(v)} data-testid={`ts-cat-${v}`}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${cat === v ? 'bg-[#E9641A] text-white border-[#E9641A]' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>
              <Icon size={11} /> {l} ({counts[v] || 0})
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select value={upCat} onChange={e => setUpCat(e.target.value)} data-testid="ts-upload-category"
            className={`rounded-lg border px-2 py-1.5 text-xs ${light ? 'bg-white border-gray-200 text-gray-700' : 'bg-[#1a0e2e] border-white/10 text-white'}`}>
            <option value="auto">Catégorie auto</option>
            {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={() => setShowGen(true)} data-testid="ts-gen-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-600 hover:bg-purple-500 text-white transition-colors">
            <Sparkles size={13} /> Générer par IA
          </button>
          <button onClick={() => fileRef.current?.click()} data-testid="ts-upload-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#E9641A] hover:bg-[#f0763a] text-white transition-colors">
            <UploadCloud size={13} /> Uploader des médias
          </button>
          <input ref={fileRef} type="file" multiple className="hidden" onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
        </div>
      </div>
      {showGen && <GenerateModal light={light} voices={voices} onClose={() => setShowGen(false)} onDone={() => { setShowGen(false); reload(); }} />}
      {uploads.map(u => (
        <div key={u.label} className={`rounded-lg border p-2 mb-2 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`}>
          <p className={`text-[11px] mb-1 ${hintCls}`}>Upload : {u.label} — {u.pct}%</p>
          <div className="h-1.5 rounded-full bg-black/20 overflow-hidden"><div className="h-full bg-[#E9641A] transition-all" style={{ width: `${u.pct}%` }} /></div>
        </div>
      ))}
      {shown.length === 0 ? (
        <div className={`rounded-xl border p-10 text-center ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/5'}`}>
          <FolderOpen size={28} className={`mx-auto mb-3 ${light ? 'text-gray-300' : 'text-white/15'}`} />
          <p className={`text-sm ${hintCls}`}>Aucun média{cat ? ' dans cette catégorie' : ''}. Uploadez vos musiques, bruitages, voix, images, vidéos ou modèles de BA — ils resteront stockés et réutilisables.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {shown.map(m => <MediaCard key={m.id} m={m} light={light} onDelete={async (x) => {
            await axios.delete(`${API}/admin/trailer-studio/media/${x.id}`, { withCredentials: true }).catch(() => {});
            reload();
          }} />)}
        </div>
      )}
    </div>
  );
}

function NewProjectPanel({ light, media, contents, voices, onClose, onCreated }) {
  const [mode, setMode] = useState('catalog');
  const [contentId, setContentId] = useState('');
  const [search, setSearch] = useState('');
  const [srcUpload, setSrcUpload] = useState(null);
  const [srcPct, setSrcPct] = useState(null);
  const [refUpload, setRefUpload] = useState(null);
  const [refPct, setRefPct] = useState(null);
  const [refMediaId, setRefMediaId] = useState('');
  const [duration, setDuration] = useState(120);
  const [style, setStyle] = useState(STYLES[0]);
  const [mood, setMood] = useState(MOODS[0]);
  const [ambiance, setAmbiance] = useState(AMBIANCES[0]);
  const [voiceover, setVoiceover] = useState(false);
  const [voiceId, setVoiceId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [mediaIds, setMediaIds] = useState([]);
  const [creating, setCreating] = useState(false);
  const srcRef = useRef(null); const refRef = useRef(null);
  const inputCls = `w-full rounded-lg border px-3 py-2 text-xs outline-none ${light ? 'bg-gray-50 border-gray-200 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`;
  const labelCls = `block text-[11px] font-bold mb-1 ${light ? 'text-gray-600' : 'text-white/60'}`;
  const hintCls = light ? 'text-gray-400' : 'text-white/35';
  const models = media.filter(m => m.category === 'modele');

  const uploadSrc = async (file) => {
    setSrcPct(0);
    try {
      const fileUrl = await uploadFileToS3(file, setSrcPct);
      setSrcUpload({ name: file.name, url: fileUrl });
    } catch (e) { alert(`Échec de l'upload du fichier vidéo : ${e?.response?.data?.detail || e.message}`); }
    setSrcPct(null);
  };
  const uploadRef = async (file) => {
    setRefPct(0);
    try {
      const fileUrl = await uploadFileToS3(file, setRefPct);
      const { data } = await axios.post(`${API}/admin/trailer-studio/media`, {
        name: file.name, category: 'modele', url: fileUrl, size: file.size, content_type: file.type,
      }, { withCredentials: true });
      setRefUpload({ name: file.name, url: fileUrl });
      setRefMediaId(data.id);
    } catch (e) { alert(`Échec de l'upload du modèle : ${e?.response?.data?.detail || e.message}`); }
    setRefPct(null);
  };

  const create = async () => {
    setCreating(true);
    try {
      const refUrl = refUpload?.url || (models.find(m => m.id === refMediaId)?.url || '');
      const body = {
        target_duration: duration, style, mood, ambiance, prompt, voiceover,
        voice_id: voiceId || undefined,
        media_ids: mediaIds, reference_url: refUrl,
      };
      if (mode === 'catalog') body.content_id = contentId;
      else { body.video_url = srcUpload.url; body.source_name = srcUpload.name; }
      await axios.post(`${API}/admin/trailer-studio/projects`, body, { withCredentials: true });
      onCreated();
    } catch (e) { alert(e?.response?.data?.detail || 'Échec de la création du projet'); }
    setCreating(false);
  };

  const filteredContents = contents.filter(c => c.has_video && (!search || c.title.toLowerCase().includes(search.toLowerCase())));
  const canCreate = (mode === 'catalog' ? !!contentId : !!srcUpload) && !creating && srcPct === null && refPct === null;

  return (
    <div className={`rounded-2xl border p-5 mb-6 ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/10'}`} data-testid="ts-new-project">
      <div className="flex items-center justify-between mb-4">
        <h2 className={`text-sm font-bold ${light ? 'text-gray-800' : 'text-white'}`}>Nouvelle bande-annonce</h2>
        <button onClick={onClose} data-testid="ts-new-close" className={hintCls}><X size={16} /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Film source</label>
          <div className="flex gap-1.5 mb-2">
            <button onClick={() => setMode('catalog')} data-testid="ts-mode-catalog" className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border ${mode === 'catalog' ? 'bg-[#E9641A] text-white border-[#E9641A]' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>Contenu de la plateforme</button>
            <button onClick={() => setMode('upload')} data-testid="ts-mode-upload" className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border ${mode === 'upload' ? 'bg-[#E9641A] text-white border-[#E9641A]' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>Uploader un fichier (max 20 Go)</button>
          </div>
          {mode === 'catalog' ? (
            <>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un titre…" className={`${inputCls} mb-1.5`} data-testid="ts-content-search" />
              <select value={contentId} onChange={e => setContentId(e.target.value)} className={inputCls} size={5} data-testid="ts-content-select">
                {filteredContents.map(c => <option key={c.id} value={c.id}>{c.title} ({c.type})</option>)}
              </select>
            </>
          ) : (
            <div>
              <button onClick={() => srcRef.current?.click()} data-testid="ts-src-upload-btn"
                className={`w-full rounded-lg border border-dashed p-4 text-xs flex items-center justify-center gap-2 ${light ? 'border-gray-300 text-gray-500' : 'border-white/20 text-white/50'}`}>
                <UploadCloud size={14} /> {srcUpload ? srcUpload.name : 'Choisir le fichier vidéo du film'}
              </button>
              <input ref={srcRef} type="file" accept="video/*" className="hidden" onChange={e => e.target.files[0] && uploadSrc(e.target.files[0])} />
              {srcPct !== null && <div className="mt-2"><p className={`text-[10px] mb-1 ${hintCls}`}>Upload… {srcPct}%</p><div className="h-1.5 rounded-full bg-black/20 overflow-hidden"><div className="h-full bg-[#E9641A]" style={{ width: `${srcPct}%` }} /></div></div>}
            </div>
          )}

          <label className={`${labelCls} mt-3`}>Bande-annonce modèle (optionnel — l'IA s'inspire du montage/rythme/style, jamais des images)</label>
          <div className="flex gap-1.5">
            <select value={refMediaId} onChange={e => { setRefMediaId(e.target.value); setRefUpload(null); }} className={inputCls} data-testid="ts-ref-select">
              <option value="">Aucun modèle</option>
              {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <button onClick={() => refRef.current?.click()} data-testid="ts-ref-upload-btn"
              className={`flex-none px-3 rounded-lg border text-[11px] font-semibold ${light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>
              <UploadCloud size={12} className="inline mr-1" />Uploader
            </button>
            <input ref={refRef} type="file" accept="video/*" className="hidden" onChange={e => e.target.files[0] && uploadRef(e.target.files[0])} />
          </div>
          {refPct !== null && <p className={`text-[10px] mt-1 ${hintCls}`}>Upload du modèle… {refPct}%</p>}
          {refUpload && <p className="text-[10px] mt-1 text-green-500">Modèle : {refUpload.name} (ajouté à la bibliothèque)</p>}
        </div>

        <div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Durée cible (s, max 180)</label>
              <input type="number" min={15} max={180} value={duration} onChange={e => setDuration(Math.min(180, +e.target.value || 0))} className={inputCls} data-testid="ts-duration" />
            </div>
            <div>
              <label className={labelCls}>Style</label>
              <select value={style} onChange={e => setStyle(e.target.value)} className={inputCls} data-testid="ts-style">{STYLES.map(s => <option key={s}>{s}</option>)}</select>
            </div>
            <div>
              <label className={labelCls}>Mood</label>
              <select value={mood} onChange={e => setMood(e.target.value)} className={inputCls} data-testid="ts-mood">{MOODS.map(s => <option key={s}>{s}</option>)}</select>
            </div>
            <div>
              <label className={labelCls}>Ambiance</label>
              <select value={ambiance} onChange={e => setAmbiance(e.target.value)} className={inputCls} data-testid="ts-ambiance">{AMBIANCES.map(s => <option key={s}>{s}</option>)}</select>
            </div>
          </div>
          <label className={`flex items-center gap-2 mt-3 text-xs ${light ? 'text-gray-700' : 'text-white/70'}`}>
            <input type="checkbox" checked={voiceover} onChange={e => setVoiceover(e.target.checked)} data-testid="ts-voiceover" className="accent-[#E9641A]" />
            Voix off cinéma (« Dans un monde… ») — l'IA écrira le script
          </label>
          {voiceover && voices.length > 0 && (
            <select value={voiceId} onChange={e => setVoiceId(e.target.value)} className={`${inputCls} mt-2`} data-testid="ts-voice-select">
              <option value="">Voix par défaut (grave cinéma — ElevenLabs)</option>
              {voices.map(v => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
            </select>
          )}
          <label className={`${labelCls} mt-3`}>Prompt — décrivez le résultat souhaité</label>
          <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="ts-prompt"
            placeholder="Ex : bande-annonce intense, ne pas montrer le méchant, finir sur un cliffhanger, gros plan sur le héros au début…" />
          <label className={`${labelCls} mt-3`}>Médias de la bibliothèque à utiliser ({mediaIds.length} sélectionné{mediaIds.length > 1 ? 's' : ''}) — sans musique sélectionnée, l'IA composera la sienne (ElevenLabs)</label>
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
            {media.filter(m => m.category !== 'modele').map(m => (
              <button key={m.id} onClick={() => setMediaIds(ids => ids.includes(m.id) ? ids.filter(i => i !== m.id) : [...ids, m.id])}
                data-testid={`ts-pick-media-${m.id}`}
                className={`px-2 py-1 rounded-full text-[10px] font-semibold border transition-colors ${mediaIds.includes(m.id) ? 'bg-[#E9641A] text-white border-[#E9641A]' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/45'}`}>
                {m.category} · {m.name.length > 24 ? m.name.slice(0, 24) + '…' : m.name}
              </button>
            ))}
            {media.filter(m => m.category !== 'modele').length === 0 && <span className={`text-[10px] ${hintCls}`}>Bibliothèque vide — uploadez d'abord vos musiques/bruitages dans l'onglet Bibliothèque.</span>}
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <button onClick={create} disabled={!canCreate} data-testid="ts-create-btn"
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-bold bg-[#E9641A] hover:bg-[#f0763a] text-white transition-colors disabled:opacity-40">
          <Wand2 size={13} /> {creating ? 'Création…' : "Lancer l'analyse IA du film"}
        </button>
      </div>
    </div>
  );
}

function ProjectCard({ p, light, onChanged }) {
  const [showPlan, setShowPlan] = useState(false);
  const [busy, setBusy] = useState(false);
  const hintCls = light ? 'text-gray-400' : 'text-white/35';
  const txtCls = light ? 'text-gray-800' : 'text-white';
  const bp = p.analysis?.blueprint;
  const active = ['queued', 'analyzing', 'render_queued', 'rendering'].includes(p.status);
  const canGenerate = ['analyzed', 'done', 'render_error'].includes(p.status) && bp;
  const badge = p.status === 'done' ? ['bg-green-500/15 text-green-500', 'Bande-annonce prête', CheckCircle2]
    : p.status === 'analyzed' ? ['bg-teal-500/15 text-teal-400', 'Analyse prête', CheckCircle2]
    : (p.status === 'error' || p.status === 'render_error') ? ['bg-red-500/15 text-red-400', 'Erreur', AlertTriangle]
    : ['bg-purple-500/15 text-purple-400',
        p.status === 'queued' ? "En file d'attente" : p.status === 'render_queued' ? 'Montage en attente' : p.status === 'rendering' ? 'Montage en cours' : 'Analyse en cours', Loader2];
  const BadgeIcon = badge[2];
  const generate = async () => {
    setBusy(true);
    try { await axios.post(`${API}/admin/trailer-studio/projects/${p.id}/generate`, {}, { withCredentials: true }); onChanged(); }
    catch (e) { alert(e?.response?.data?.detail || 'Échec du lancement du montage'); }
    setBusy(false);
  };
  const setOfficial = async () => {
    if (!window.confirm('Définir cette bande-annonce comme bande-annonce officielle du contenu sur la plateforme ?')) return;
    setBusy(true);
    try { await axios.post(`${API}/admin/trailer-studio/projects/${p.id}/set-official`, {}, { withCredentials: true }); alert('Bande-annonce officielle mise à jour ✓'); }
    catch (e) { alert(e?.response?.data?.detail || 'Échec'); }
    setBusy(false);
  };
  return (
    <div className={`rounded-xl border p-4 ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`} data-testid={`ts-project-${p.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${badge[0]}`}>
              <BadgeIcon size={10} className={active ? 'animate-spin' : ''} /> {badge[1]}
            </span>
            <span className={`text-sm font-bold ${txtCls}`}>{p.title}</span>
            <span className={`text-[10px] ${hintCls}`}>{p.target_duration}s · {p.style} · {p.mood}{p.voiceover ? ' · voix off' : ''}{p.reference_url ? ' · modèle fourni' : ''}</span>
          </div>
          {active && (
            <div className="mt-2">
              <p className={`text-[11px] mb-1 ${hintCls}`}>{p.step}</p>
              <div className="h-1.5 rounded-full bg-black/20 overflow-hidden"><div className="h-full bg-[#E9641A] transition-all" style={{ width: `${p.progress || 0}%` }} /></div>
            </div>
          )}
          {(p.status === 'error' || p.status === 'render_error') && <p className="text-[11px] mt-1 text-red-400">{p.error}</p>}
          {p.status === 'done' && p.trailer_url && (
            <div className="mt-2" data-testid={`ts-preview-${p.id}`}>
              <video src={p.trailer_url} controls className="w-full max-w-md rounded-lg bg-black" preload="metadata" />
              <p className={`text-[10px] mt-1 ${hintCls}`}>Durée : {Math.round(p.trailer_duration || 0)}s</p>
            </div>
          )}
          {bp && !active && (
            <div className="mt-2">
              <p className={`text-[11px] ${hintCls}`}>
                {p.analysis.scenes_count} scènes analysées · {p.analysis.transcript_segments} segments de dialogue · film de {Math.round((p.analysis.duration || 0) / 60)} min
                {p.analysis.reference_style?.shots_count ? ` · modèle : ${p.analysis.reference_style.shots_count} plans étudiés` : ''}
              </p>
              <p className={`text-xs mt-1 italic ${light ? 'text-gray-600' : 'text-white/60'}`}>« {bp.logline} »</p>
              <button onClick={() => setShowPlan(!showPlan)} data-testid={`ts-plan-toggle-${p.id}`} className="text-[11px] font-semibold text-purple-400 hover:text-purple-300 mt-1 flex items-center gap-1">
                <BookOpen size={11} /> {showPlan ? 'Masquer' : 'Voir'} le plan de montage ({(bp.edl || []).length} plans, ~{bp.estimated_duration || '?'}s)
              </button>
              {showPlan && (
                <div className={`mt-2 rounded-lg p-3 text-[11px] space-y-1.5 ${light ? 'bg-gray-50 text-gray-600' : 'bg-white/[0.04] text-white/65'}`}>
                  <p><b>Histoire :</b> {bp.story}</p>
                  {bp.voiceover_script && <p><b>Voix off :</b> {bp.voiceover_script}</p>}
                  {(bp.title_cards || []).length > 0 && <p><b>Cartons :</b> {bp.title_cards.join(' · ')}</p>}
                  {bp.music_notes && <p><b>Musique :</b> {bp.music_notes}</p>}
                  <div className="max-h-44 overflow-y-auto space-y-0.5 pt-1">
                    {(bp.edl || []).map((s, i) => (
                      <p key={i} className="font-mono text-[10px]">Acte {s.act} · {Math.floor(s.start / 60)}:{String(Math.floor(s.start % 60)).padStart(2, '0')}→{Math.floor(s.end / 60)}:{String(Math.floor(s.end % 60)).padStart(2, '0')} [{s.effect}] {s.note}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 flex-none">
          {canGenerate && (
            <button onClick={generate} disabled={busy} data-testid={`ts-generate-${p.id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-green-600/90 hover:bg-green-500 text-white transition-colors disabled:opacity-50">
              <Film size={11} /> {p.status === 'done' ? 'Régénérer le montage' : 'Générer le montage'}
            </button>
          )}
          {p.status === 'done' && p.trailer_url && (
            <>
              <a href={p.trailer_url} download target="_blank" rel="noreferrer" data-testid={`ts-download-${p.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-blue-600/90 hover:bg-blue-500 text-white transition-colors">
                <UploadCloud size={11} className="rotate-180" /> Télécharger MP4
              </a>
              {p.source?.content_id && (
                <button onClick={setOfficial} disabled={busy} data-testid={`ts-official-${p.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#E9641A] hover:bg-[#f0763a] text-white transition-colors disabled:opacity-50">
                  <CheckCircle2 size={11} /> Définir comme BA officielle
                </button>
              )}
            </>
          )}
          {!active && (
            <button onClick={async () => { await axios.post(`${API}/admin/trailer-studio/projects/${p.id}/analyze`, {}, { withCredentials: true }).catch(() => {}); onChanged(); }}
              data-testid={`ts-reanalyze-${p.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border ${light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>
              <RotateCcw size={11} /> Relancer l'analyse
            </button>
          )}
          <button onClick={async () => { if (window.confirm('Supprimer ce projet ?')) { await axios.delete(`${API}/admin/trailer-studio/projects/${p.id}`, { withCredentials: true }).catch(() => {}); onChanged(); } }}
            data-testid={`ts-delete-${p.id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-red-600/90 hover:bg-red-500 text-white transition-colors">
            <Trash2 size={11} /> Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}
export function TrailerStudioTab({ theme }) {
  const light = theme === 'light';
  const [sub, setSub] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [media, setMedia] = useState([]);
  const [counts, setCounts] = useState({});
  const [contents, setContents] = useState([]);
  const [voices, setVoices] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const txtCls = light ? 'text-gray-800' : 'text-white';
  const hintCls = light ? 'text-gray-400' : 'text-white/35';

  const loadProjects = useCallback(() => {
    axios.get(`${API}/admin/trailer-studio/projects`, { withCredentials: true })
      .then(r => setProjects(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const loadMedia = useCallback(() => {
    axios.get(`${API}/admin/trailer-studio/media`, { withCredentials: true })
      .then(r => { setMedia(r.data.items || []); setCounts(r.data.counts || {}); }).catch(() => {});
  }, []);

  useEffect(() => {
    loadProjects(); loadMedia();
    axios.get(`${API}/admin/trailer-studio/contents`, { withCredentials: true }).then(r => setContents(r.data || [])).catch(() => {});
    axios.get(`${API}/admin/trailer-studio/voices`, { withCredentials: true }).then(r => setVoices(r.data?.voices || [])).catch(() => {});
  }, [loadProjects, loadMedia]);

  useEffect(() => {
    if (!projects.some(p => ['queued', 'analyzing', 'render_queued', 'rendering'].includes(p.status))) return;
    const t = setInterval(loadProjects, 5000);
    return () => clearInterval(t);
  }, [projects, loadProjects]);

  return (
    <div data-testid="trailer-studio-tab">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <h1 className={`text-2xl font-bold ${txtCls}`}>Bandes annonces</h1>
        <div className="flex gap-1.5">
          <button onClick={() => setSub('projects')} data-testid="ts-sub-projects"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${sub === 'projects' ? 'bg-[#E9641A] text-white border-[#E9641A]' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>Projets ({projects.length})</button>
          <button onClick={() => setSub('library')} data-testid="ts-sub-library"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${sub === 'library' ? 'bg-[#E9641A] text-white border-[#E9641A]' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>Bibliothèque ({media.length})</button>
        </div>
      </div>
      <p className={`text-xs mb-6 ${hintCls}`}>L'IA lit le film en entier (lecture rapide), analyse les scènes et les dialogues, conçoit un plan de montage hollywoodien (≤ 3 min) selon votre style, mood, ambiance et prompt, puis génère la bande-annonce finale : cuts, fondus au noir, arrêts sur image, cartons, musique, bruitages et voix off.</p>

      {sub === 'library' ? (
        <MediaLibrary light={light} media={media} counts={counts} reload={loadMedia} voices={voices} />
      ) : (
        <>
          {!showNew && (
            <button onClick={() => setShowNew(true)} data-testid="ts-new-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold bg-[#E9641A] hover:bg-[#f0763a] text-white transition-colors mb-5">
              <Plus size={14} /> Nouvelle bande-annonce
            </button>
          )}
          {showNew && (
            <NewProjectPanel light={light} media={media} contents={contents} voices={voices}
              onClose={() => setShowNew(false)}
              onCreated={() => { setShowNew(false); loadProjects(); }} />
          )}
          {loading ? (
            <p className={`text-center text-sm py-10 ${hintCls}`}>Chargement…</p>
          ) : projects.length === 0 ? (
            <div className={`rounded-xl border p-10 text-center ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/5'}`} data-testid="ts-empty">
              <Clapperboard size={28} className={`mx-auto mb-3 ${light ? 'text-gray-300' : 'text-white/15'}`} />
              <p className={`text-sm ${hintCls}`}>Aucun projet. Créez votre première bande-annonce IA.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {projects.map(p => <ProjectCard key={p.id} p={p} light={light} onChanged={loadProjects} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
